import { dedent } from 'ts-dedent';
import { DataWarehouseConnection, EXTENSIONS_TABLE_NAME } from '..';
import { ReadTable } from '../../../components/readtable';
import { ColumnDataType, Provider, TableOptimizationColumnDataType, WorkflowCodeType, WorkflowComponentParamType } from '../../../types';
import { OUTPUT_TABLE_FQN_VAR_NAME, OUTPUT_TABLE_NAME_VAR_NAME, formatVariableValue, getVariablesUsedInQuery, isNumericIntegerValue } from '../../../utils';
export class SFConnection extends DataWarehouseConnection {
    constructor(name, tempStoragePath) {
        super(name, tempStoragePath, Provider.Snowflake);
    }
    castValue(value, type) {
        return `(${value})::${type}`;
    }
    getTempSelectQuery(select, dryRun) {
        if (dryRun) {
            return dedent `SELECT * FROM
                      (
                        ${select}
                      )
                    WHERE 1=0`;
        }
        else {
            return select;
        }
    }
    getOutputTableName(workflowId, componentName, params, output, codeType) {
        const name = super.getOutputTableName(workflowId, componentName, params, output, codeType);
        // in SF we use the full names also for temp tables
        if (codeType === WorkflowCodeType.StoredProcedure) {
            return `${this.tempStoragePath}.${name}`.toUpperCase();
        }
        else {
            return name.toUpperCase();
        }
    }
    getCreateTempTableQuery(tablename, select, codeType, optimizeBy, addOptimizationStats) {
        select = dedent(select);
        const dryRun = codeType === WorkflowCodeType.DryRun;
        let create = '';
        if (codeType === WorkflowCodeType.StoredProcedure) {
            create = `CREATE OR REPLACE TEMPORARY TABLE`;
        }
        else if (codeType === WorkflowCodeType.FullNoCache) {
            create = 'CREATE OR REPLACE TRANSIENT TABLE';
        }
        else {
            create = 'CREATE TRANSIENT TABLE IF NOT EXISTS';
        }
        let query = '';
        if (!dryRun && optimizeBy?.sfSearchOptimizationColumn) {
            query = dedent `${create} ${tablename}
                AS
                SELECT * FROM (
                  ${this.getTempSelectQuery(select, dryRun)}
                )
                ORDER BY ST_GEOHASH(${optimizeBy.sfSearchOptimizationColumn.name});
                BEGIN
                  ALTER TABLE ${tablename}
                    ADD search optimization
                    ON GEO(${optimizeBy.sfSearchOptimizationColumn.name});
                EXCEPTION WHEN OTHER THEN
                    NULL;
                END;`;
        }
        else {
            query = dedent `${create} ${tablename}
                 AS
                  ${this.getTempSelectQuery(select, dryRun)};`;
        }
        if (!dryRun &&
            optimizeBy?.column?.type === TableOptimizationColumnDataType.NonGeo) {
            query += `ALTER TABLE ${tablename} CLUSTER BY (${optimizeBy.column.name});`;
        }
        return query;
    }
    getCreateTableQuery(tablename, select, codeType, optimizeBy, addOptimizationStats) {
        return this.getCreateTempTableQuery(tablename, select, codeType, optimizeBy, addOptimizationStats)
            .replace(RegExp('TRANSIENT ', 'g'), '')
            .replace(RegExp('TEMPORARY ', 'g'), '');
    }
    getCreateViewQuery(viewname, select, codeType) {
        select = dedent(select);
        let query = dedent `CREATE VIEW IF NOT EXISTS ${viewname}
                        AS
                          ${this.getTempSelectQuery(select, codeType === WorkflowCodeType.DryRun)};`;
        return query;
    }
    getCreateTempViewQuery(tablename, select, codeType) {
        return this.getCreateViewQuery(tablename, select, codeType);
    }
    wrapComponentCodeInFlowControlBlock(query, codeType) {
        if (codeType === WorkflowCodeType.DryRun) {
            return dedent `
          BEGIN
            ${query ?? 'SELECT 1;'}
          EXCEPTION
          WHEN statement_error THEN
            SELECT 1;
            -- do nothing
          WHEN OTHER THEN
            SELECT 1;
            -- do nothing
          END;`;
        }
        else {
            return dedent `
          BEGIN
            ${query}
          END;`;
        }
    }
    getStoredProcedureOutputTableNameExpression(workflow, full) {
        const publicVars = workflow.variables.filter((v) => v.public);
        let root = workflow.getStoredProcedureOutputTableFullName(this);
        if (!full) {
            root = root.split('.').pop();
        }
        if (publicVars.length === 0) {
            return `'${root}_' || SUBSTRING(MD5(''), 1, 16)`;
        }
        else {
            return `'${root}_' || SUBSTRING(MD5(${publicVars
                .map((v) => this.quoteVariableName(v.name))
                .join(" || ',' || ")}), 1, 16)`;
        }
    }
    wrapCodeInIfTableExistsBlock(query, outputTables, codeType, outputTablesExpressions, variablePrefix) {
        variablePrefix = variablePrefix || '';
        if ([WorkflowCodeType.DryRun, WorkflowCodeType.StoredProcedure].includes(codeType)) {
            return query;
        }
        else if (codeType === WorkflowCodeType.FullNoCache) {
            return dedent `
        ${outputTables
                .map((x) => `DROP TABLE IF EXISTS ${this.quoteName(x)};`)
                .join('\n')}
        ${query}
      `;
        }
        else {
            const outputTablesLiteralNames = outputTables.map((x) => `'\\'${x.split('.').pop()?.toUpperCase()}\\''`);
            const outputTablesString = [
                ...outputTablesLiteralNames,
                ...(outputTablesExpressions?.map((v) => `'\\'' || UPPER(${v}) || '\\''`) || [])
            ].join(', ');
            const [database, schema] = this.tempStoragePath.split('.');
            return dedent `
          DECLARE
            ${variablePrefix}query STRING;
            ${variablePrefix}tablesCount INTEGER;
            ${variablePrefix}tablesNames STRING;
          BEGIN
            ${variablePrefix}tablesNames := ${outputTablesString};
            ${variablePrefix}query := '
              SELECT COUNT(1) AS cnt
              FROM
                ${database}.INFORMATION_SCHEMA.TABLES
              WHERE
                table_name IN (' || ${variablePrefix}tablesNames || ')
                AND table_schema ILIKE ''${schema}''
            ';
            DECLARE
              rs resultset DEFAULT (EXECUTE IMMEDIATE :${variablePrefix}query);
              cur CURSOR FOR rs;
            BEGIN
              FOR result_row IN cur DO
                ${variablePrefix}tablesCount := result_row.cnt;
              END FOR;
            END;
            IF (${variablePrefix}tablesCount < ${outputTables.length + (outputTablesExpressions?.length || 0)}) THEN
              BEGIN
                ${query}
              END;
            END IF;
          END;`;
        }
    }
    async getWorkflowCode(workflow, envVars, codeType, schemas) {
        const queries = await workflow.getSubqueries(this, envVars, codeType, schemas);
        const executable = queries.some((q) => q.query);
        let query = '';
        if (executable) {
            if (codeType !== WorkflowCodeType.StoredProcedure) {
                query = queries.map((q) => q.query).join('\n\n');
                const type = codeType === WorkflowCodeType.DryRun ? 'DRY-RUN' : 'RUN';
                query = dedent `
                /*
                ${workflow.workflowMetadataAsCommentString(codeType)}
                */
                BEGIN
                  ${query}
                END;`;
                query = this.replaceVariablesInScriptQuery(query, workflow.variables);
            }
            else {
                return this._getProcedureBody(workflow, queries, schemas);
            }
        }
        return query;
    }
    replaceVariablesInStoredProcedureQuery(variables, query, requiresDynamicSQL) {
        const varnames = getVariablesUsedInQuery(query, variables);
        if (varnames.length === 0) {
            return query;
        }
        if (requiresDynamicSQL) {
            query = dedent(query).replace(/'/g, "\\'");
            for (const varname of varnames) {
                query = query.replace(RegExp(`@${varname}`, 'g'), `' || ${this.quoteVariableName(varname)} || '`);
            }
            query = this.executeDynamicSQL({ sql: query, addQuotes: true });
        }
        else {
            for (const varname of varnames) {
                query = query.replace(RegExp(`@${varname}`, 'g'), `:${varname}`);
            }
        }
        return query;
    }
    paramTypeToDataProviderType(inputType, inputValue) {
        switch (inputType) {
            case WorkflowComponentParamType.String:
                return 'STRING';
            case WorkflowComponentParamType.Number:
                return isNumericIntegerValue(inputValue) ? 'INT' : 'FLOAT';
            case WorkflowComponentParamType.Boolean:
                return 'BOOLEAN';
            default:
                return 'STRING';
        }
    }
    columnTypeToDataProviderType(colType) {
        switch (colType) {
            case ColumnDataType.String:
                return 'STRING';
            case ColumnDataType.Number:
                return 'FLOAT';
            case ColumnDataType.Boolean:
                return 'BOOLEAN';
            case ColumnDataType.Geography:
                return 'GEOGRAPHY';
            case ColumnDataType.Binary:
                return 'BINARY';
            case ColumnDataType.Date:
                return 'DATE';
            case ColumnDataType.Time:
                return 'TIME';
            case ColumnDataType.Timestamp:
                return 'TIMESTAMP';
            default:
                return 'STRING';
        }
    }
    wrapCodeInProcedure(name, workflow, body, includeDiagram, isImmutable) {
        const procedureParams = workflow.variables
            .filter((v) => v.public)
            .map((input) => `${this.quoteVariableName(input.name)} ${this.paramTypeToDataProviderType(input.type, input.value)}`);
        const declareVariables = workflow.variables
            .filter((v) => !v.public)
            .map((input) => `${this.quoteVariableName(input.name)} ${this.paramTypeToDataProviderType(input.type, input.value)} DEFAULT ${formatVariableValue(input)};`);
        const defaultValues = workflow.variables
            .filter((v) => v.public)
            .map((input) => `${this.quoteVariableName(input.name)} := COALESCE(${this.quoteVariableName(input.name)}, ${formatVariableValue(input)});`);
        const hasOutput = workflow.hasStoredProcedureOutputTable();
        let outputTableVars = '';
        if (hasOutput || isImmutable) {
            declareVariables.push(`${OUTPUT_TABLE_NAME_VAR_NAME} STRING;`);
            declareVariables.push(`${OUTPUT_TABLE_FQN_VAR_NAME} STRING;`);
            const outputTableNameVariable = `${OUTPUT_TABLE_NAME_VAR_NAME} := ${this.getStoredProcedureOutputTableNameExpression(workflow, false)};`;
            const outputTableFqnVariable = `${OUTPUT_TABLE_FQN_VAR_NAME} := ${this.getStoredProcedureOutputTableNameExpression(workflow, true)};`;
            outputTableVars = dedent `${outputTableNameVariable}
                               ${outputTableFqnVariable}`;
        }
        const comment = this._getStoredProcedureEmbeddedComment(workflow, isImmutable);
        const returnTableName = hasOutput
            ? dedent `RETURN ${OUTPUT_TABLE_FQN_VAR_NAME};`
            : '';
        return dedent `
                CREATE OR REPLACE PROCEDURE
                  ${this.tempStoragePath}.${name}(
                    ${procedureParams.join(',\n')}
                  )
                  RETURNS VARCHAR
                  LANGUAGE SQL
                  AS
                  $$
                  ${includeDiagram ? comment : ''}
                  ${declareVariables.length > 0
            ? dedent `
                        DECLARE
                          ${declareVariables.join('\n')}`
            : ''}
                  BEGIN
                    ${defaultValues.join('\n')}
                    ${outputTableVars}
                    ${body}
                    ${returnTableName}
                  END;
                  $$`;
    }
    getCheckStoredProcedureExistsQueries(workflow, isImmutable) {
        const versionId = workflow.workflowVersionHash();
        const paramsId = workflow.workflowParamsHash();
        const [database, schema] = this.tempStoragePath.split('.');
        return [
            dedent `
          DECLARE
            procedureExists BOOLEAN;
            isCurrentVersion BOOLEAN;
            res RESULTSET;
          BEGIN
            SELECT COUNT(*) > 0 INTO :procedureExists
            FROM ${database}.INFORMATION_SCHEMA.PROCEDURES
            WHERE PROCEDURE_NAME = '${workflow
                .getStoredProcedureName()
                .toUpperCase()}'
              AND
              PROCEDURE_SCHEMA = '${schema.toUpperCase()}';
            IF (procedureExists) THEN
              SELECT TRUE
              INTO :isCurrentVersion
              FROM ${database}.INFORMATION_SCHEMA.PROCEDURES
              WHERE PROCEDURE_NAME = '${workflow
                .getStoredProcedureName()
                .toUpperCase()}'
                AND
                PROCEDURE_SCHEMA = '${schema.toUpperCase()}'
                AND
                CONTAINS(PROCEDURE_DEFINITION, '"versionId":"${versionId}"')
                AND
                CONTAINS(PROCEDURE_DEFINITION, '"paramsId":"${paramsId}"')
                AND
                CONTAINS(PROCEDURE_DEFINITION, '"isImmutable":${isImmutable}');
              IF (isCurrentVersion) THEN
                res := (EXECUTE IMMEDIATE 'select \\\'exists\\\' as "status"');
                RETURN TABLE(res);
              ELSE
                res := (EXECUTE IMMEDIATE 'select \\\'outdated\\\' as "status"');
                RETURN TABLE(res);
              END IF;
            ELSE
              res := (EXECUTE IMMEDIATE 'select \\\'missing\\\' as "status"');
              RETURN TABLE(res);
            END IF;
          END;
            `
        ];
    }
    extractErrorLineNumberFromApiResponse(response) {
        const match = response.match(/line (\d+) at/);
        if (match) {
            return parseInt(match[1]) + 6; // header lines are not considered by Snowflake, so we have to add them here
        }
        else {
            return null;
        }
    }
    getSourceTablePermissionsQuery(workflow) {
        const conditions = {};
        const tablenames = [];
        const selects = [];
        for (const [componentName, component] of Object.entries(workflow.components)) {
            if (component.metadata.name === new ReadTable().metadata.name) {
                const value = workflow.componentValues[componentName]['source'];
                const tablename = value.value;
                tablenames.push(tablename);
                const [catalog, schema, name] = tablename.toUpperCase().split('.');
                if (!(catalog in conditions)) {
                    conditions[catalog] = [];
                }
                conditions[catalog].push(dedent `(TABLE_CATALOG = '${catalog}' AND TABLE_SCHEMA = '${schema}' AND TABLE_NAME = '${name}')`);
                tablename;
            }
        }
        if (tablenames.length === 0) {
            return '';
        }
        const accessible = Object.keys(conditions)
            .map((k) => {
            return dedent `SELECT TABLE_CATALOG || '.' || TABLE_SCHEMA || '.' || TABLE_NAME AS name
                FROM ${k}.INFORMATION_SCHEMA.TABLES
                WHERE ${conditions[k].join('\nOR\n')}`;
        })
            .join('\nUNION ALL\n');
        const query = dedent `WITH _accessible AS (
              ${accessible}
            ),
            _names AS (
              SELECT name
              FROM (
                ${tablenames
            .map((x) => dedent `SELECT '${x}' AS name`)
            .join('\nUNION ALL\n')}
              )
            )
            SELECT ARRAY_AGG(name) AS noAccess
            FROM _names n
            WHERE NOT EXISTS (
              SELECT 1
              FROM _accessible a
              WHERE n.name = a. name
            );`;
        return query;
    }
    getTempStorageSetupQueries(options) {
        const [database, schema] = this.tempStoragePath.split('.');
        return [
            dedent `
      DECLARE
        schemaExists BOOLEAN;
        res RESULTSET;
      BEGIN
        SELECT COUNT(*) != 0 INTO :schemaExists FROM ${database}.INFORMATION_SCHEMA.SCHEMATA WHERE SCHEMA_NAME ILIKE '${schema}';
        IF (schemaExists) THEN
          BEGIN
            CREATE TABLE IF NOT EXISTS ${this.tempStoragePath}.__TESTTABLE AS (SELECT 1 AS N);
            DROP TABLE IF EXISTS ${this.tempStoragePath}.__TESTTABLE;
            res := (EXECUTE IMMEDIATE 'select \\\'{"success": true}\\\' as "result"');
            RETURN TABLE(res);
          EXCEPTION WHEN OTHER THEN
            res := (EXECUTE IMMEDIATE 'select \\\'{"success": false, "error": "temp_storage_create_tables"}\\\' as "result"');
            RETURN TABLE(res);
          END;
        ELSE
          BEGIN
            CREATE SCHEMA IF NOT EXISTS ${this.tempStoragePath};
            res := (EXECUTE IMMEDIATE 'select \\\'{"success": true}\\\' as "result"');
            RETURN TABLE(res);
          EXCEPTION WHEN OTHER THEN
            res := (EXECUTE IMMEDIATE 'select \\\'{"success": false, "error": "temp_storage_access_create"}\\\' as "result"');
            RETURN TABLE(res);
          END;
        END IF;
      EXCEPTION WHEN OTHER THEN
        res := (EXECUTE IMMEDIATE 'select \\\'{"success": false, "error": "temp_storage_access_create"}\\\' as "result"');
        RETURN TABLE(res);
      END;`
        ];
    }
    getTempStorageCleanUpQuery() {
        const [database, schema] = this.tempStoragePath?.split('.');
        return dedent `
      DECLARE
        statements STRING;
      BEGIN
        LET res resultset := (EXECUTE IMMEDIATE '
          SELECT
            LISTAGG(''DROP TABLE ''|| TABLE_CATALOG || ''.'' || TABLE_SCHEMA || ''.'' || TABLE_NAME || '';'')
          FROM
            ${this.applyCapitalization(database)}.INFORMATION_SCHEMA.TABLES
          WHERE TABLE_SCHEMA = ''${this.applyCapitalization(schema)}''
          AND CREATED < DATEADD(''day'', -30, CURRENT_TIMESTAMP)
        ');
        LET cur_res CURSOR FOR res;
        OPEN cur_res;
        FETCH cur_res INTO statements;
        IF (statements != '') THEN
          EXECUTE IMMEDIATE 'BEGIN\\n' || :statements || '\\nEND';
        END IF;
      END;`;
    }
    getStoredProcedureCacheInvalidationQueries(workflow) {
        const [database, schema] = this.tempStoragePath?.split('.');
        return [
            dedent `
      DECLARE
        statements STRING;
      BEGIN
        LET res resultset := (EXECUTE IMMEDIATE '
          SELECT
            LISTAGG(''DROP TABLE ''|| TABLE_CATALOG || ''.'' || TABLE_SCHEMA || ''.'' || TABLE_NAME || '';'')
          FROM
            ${this.applyCapitalization(database)}.INFORMATION_SCHEMA.TABLES
          WHERE TABLE_SCHEMA = ''${this.applyCapitalization(schema)}''
          AND TABLE_NAME ILIKE ''${workflow.getStoredProcedureOutputTableName(this)}%''
        ');
        LET cur_res CURSOR FOR res;
        OPEN cur_res;
        FETCH cur_res INTO statements;
        IF (statements != '') THEN
          EXECUTE IMMEDIATE 'BEGIN\\n' || :statements || '\\nEND';
        END IF;
      END;`
        ];
    }
    getTableListQuery(filter) {
        const [database, schema] = this.tempStoragePath.split('.');
        return dedent `
      SELECT TABLE_NAME AS "table_name"
      FROM ${this.applyCapitalization(database)}.INFORMATION_SCHEMA.TABLES
      WHERE TABLE_SCHEMA = '${this.applyCapitalization(schema)}' AND TABLE_NAME LIKE '${filter}';`;
    }
    dataProviderTypeToColumnType(type) {
        switch (type) {
            case 'NUMBER':
            case 'FLOAT':
                return ColumnDataType.Number;
            case 'BOOLEAN':
                return ColumnDataType.Boolean;
            case 'GEOMETRY':
                return ColumnDataType.Geometry;
            case 'GEOGRAPHY':
                return ColumnDataType.Geography;
            case 'DATE':
                return ColumnDataType.Date;
            case 'TIME':
                return ColumnDataType.Time;
            case 'TIMESTAMP':
            case 'TIMESTAMPTZ':
                return ColumnDataType.Timestamp;
            case 'BINARY':
                return ColumnDataType.Binary;
            default:
                return ColumnDataType.String;
        }
    }
    quoteVariableName(name) {
        return `"${name.toUpperCase()}"`;
    }
    applyCapitalization(tablename) {
        return tablename.toUpperCase();
    }
    getExtensionComponentsQuery() {
        const extensionsTable = `${this.quoteName(this.tempStoragePath)}.${EXTENSIONS_TABLE_NAME}`;
        return dedent `
          SELECT
            name AS "name", metadata AS "metadata"
          FROM
            ${extensionsTable}
          `;
    }
    getExtensionUninstallQuery(name) {
        return dedent `
      DECLARE
          procedures STRING;
      BEGIN
          BEGIN
            procedures := (
                SELECT procedures
                FROM ${this.quoteName(this.tempStoragePath)}.${EXTENSIONS_TABLE_NAME}
                WHERE name = '${name}'
            );
          EXCEPTION
            WHEN OTHER THEN
              procedures := '';
          END;
          BEGIN
              EXECUTE IMMEDIATE 'DROP PROCEDURE IF EXISTS ${this.quoteName(this.tempStoragePath)}.'
                  || REPLACE(
                      :procedures,
                      ';',
                      ';DROP PROCEDURE IF EXISTS ${this.quoteName(this.tempStoragePath)}.'
                    );
          EXCEPTION
              WHEN OTHER THEN
                  NULL;
          END;
          DELETE FROM ${this.quoteName(this.tempStoragePath)}.${EXTENSIONS_TABLE_NAME}
          WHERE name = '${name}';
        END;`;
    }
    adaptComponent(component) {
        const whiteList = ['native.geometrytogeography'];
        if (!whiteList.includes(component.metadata.name)) {
            for (const input of component.inputs) {
                if (input.type === WorkflowComponentParamType.Column &&
                    input.dataType) {
                    input.dataType = input.dataType.filter((x) => x !== ColumnDataType.Geometry);
                }
            }
        }
        component.inputs = component.inputs.filter((x) => x.providers == null || x.providers.includes(Provider.Snowflake));
    }
    getOutputSchemasQueries(tableNames) {
        const tempStoragePath = this.tempStoragePath.toUpperCase();
        const database = tempStoragePath.split('.')[0];
        const schema = tempStoragePath.split('.')[1];
        return dedent `
      WITH cols AS (
        SELECT
          table_name,
          column_name AS name,
          data_type AS db_type,
          CASE data_type
            WHEN 'NUMBER' THEN '${ColumnDataType.Number}'
            WHEN 'FLOAT' THEN '${ColumnDataType.Number}'
            WHEN 'BOOLEAN' THEN '${ColumnDataType.Boolean}'
            WHEN 'GEOGRAPHY' THEN '${ColumnDataType.Geography}'
            WHEN 'GEOMETRY' THEN '${ColumnDataType.Geometry}'
            WHEN 'DATE' THEN '${ColumnDataType.Date}'
            WHEN 'TIME' THEN '${ColumnDataType.Time}'
            WHEN 'TIMESTAMP' THEN '${ColumnDataType.Timestamp}'
            WHEN 'TIMESTAMPTZ' THEN '${ColumnDataType.Timestamp}'
            WHEN 'BINARY' THEN '${ColumnDataType.Binary}'
            ELSE '${ColumnDataType.String}'
          END AS type,
          ordinal_position
        FROM ${database}.INFORMATION_SCHEMA.COLUMNS
        WHERE
          table_name IN (${tableNames
            .map((t) => `'${t.toUpperCase()}'`)
            .join(', ')})
          AND table_schema = '${schema}'
      )
      SELECT
        CONCAT('${tempStoragePath}.', table_name) AS fqn,
        TO_JSON(
          ARRAY_AGG(
            OBJECT_CONSTRUCT(
              'name', name,
              'type', type,
              'db_type', db_type
            )
          )
          WITHIN GROUP (ORDER BY ordinal_position ASC)
        ) AS schema
      FROM cols
      GROUP BY table_name;`;
    }
    prepareSchemaQueries(queries) {
        const last = queries.pop();
        return [`BEGIN\n${queries.join('\n')}\nEND;`, last];
    }
}
